import { Head, Link } from '@inertiajs/react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Calendar, User, ArrowRight, BookOpen } from 'lucide-react';

interface BlogPost {
  id: number;
  title: string;
  content: string;
  author: string;
  published_at: string;
  excerpt: string;
}

interface Pagination {
  current_page: number;
  total_pages: number;
  total_count: number;
}

interface Props {
  blog_posts: BlogPost[];
  pagination: Pagination;
}

export default function BlogIndex({ blog_posts, pagination }: Props) {
  return (
    <>
      <Head title="Blog - Ethos Santé" />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-green-50 to-blue-50">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-green-600 mr-3" />
              <h1 className="text-4xl font-bold text-gray-900">Blog</h1>
            </div>
            <p className="text-xl text-gray-600 mb-8">
              Découvrez nos articles sur la santé, la kinésithérapie
            </p>
            <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                <span>{pagination.total_count} article{pagination.total_count > 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center">
                <User className="w-4 h-4 mr-1" />
                <span>Équipe Ethos Santé</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Articles Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {blog_posts.length > 0 ? (
            <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
              {blog_posts.map((post) => (
                <Card key={post.id} className="group hover:shadow-lg transition-all duration-300 overflow-hidden">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="secondary" className="text-xs">
                        {format(new Date(post.published_at), 'MMM yyyy', { locale: fr })}
                      </Badge>
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    </div>
                    <CardTitle className="text-xl font-bold group-hover:text-green-600 transition-colors line-clamp-2">
                      <Link href={`/blog/${post.id}`} className="hover:no-underline">
                        {post.title}
                      </Link>
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="pt-0">
                    <div className="flex items-center text-sm text-gray-500 mb-4">
                      <User className="w-4 h-4 mr-1" />
                      <span>{post.author}</span>
                      <span className="mx-2">•</span>
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>{format(new Date(post.published_at), 'd MMM yyyy', { locale: fr })}</span>
                    </div>
                    
                    <p className="text-gray-600 mb-6 line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    
                    <div className="flex items-center">
                      <Link href={`/blog/${post.id}`}>
                        <Button variant="ghost" size="sm" className="group-hover:text-green-600">
                          Lire l'article
                          <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="text-center py-16">
              <CardContent>
                <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun article pour le moment</h3>
                <p className="text-gray-600">Revenez bientôt pour découvrir nos nouveaux articles !</p>
              </CardContent>
            </Card>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="mt-12 flex justify-center">
              <Card className="p-2">
                <div className="flex items-center space-x-2">
                  {pagination.current_page > 1 && (
                    <Link href={`/blog?page=${pagination.current_page - 1}`}>
                      <Button variant="outline" size="sm">
                        Précédent
                      </Button>
                    </Link>
                  )}
                  
                  {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
                    <Link key={page} href={`/blog?page=${page}`}>
                      <Button
                        variant={page === pagination.current_page ? "default" : "outline"}
                        size="sm"
                        className="min-w-[40px]"
                      >
                        {page}
                      </Button>
                    </Link>
                  ))}
                  
                  {pagination.current_page < pagination.total_pages && (
                    <Link href={`/blog?page=${pagination.current_page + 1}`}>
                      <Button variant="outline" size="sm">
                        Suivant
                      </Button>
                    </Link>
                  )}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="bg-gray-50 border-t">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Restez informé de nos derniers articles
            </h2>
            <p className="text-gray-600 mb-6">
              Recevez nos articles directement dans votre boîte mail
            </p>
            <div className="flex max-w-md mx-auto">
              <input
                type="email"
                placeholder="Votre adresse email"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <Button className="rounded-l-none">
                S'abonner
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
} 
